import { useState, useEffect } from "react";
import { Button, Box, Container } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { DJSimpleCodeBlock } from "../../main/components/codeblock";
import {
  IProgress,
  ISessionProps,
} from "../../main/components/IProgress";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography } from "@appsflyer/fe-ui-core";
import transition from "../../main/components/transition";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import { amplitudeTrack_continue } from "../../services/utils";

// The Flutter plugin snippet stays pinned to the 6.x line, so we look for the
// newest stable 6 release rather than the newest release overall.
const SDK_MAJOR_PREFIX = "6.";
const FALLBACK_SDK_VERSION = "6.18.1";
const RELEASES_URL =
  "https://api.github.com/repos/AppsFlyerSDK/appsflyer-flutter-plugin/releases";
const RELEASES_PER_PAGE = 100;
const MAX_RELEASE_PAGES = 5;

// Tags in this repo aren't uniform: most are bare (6.18.1), some carry a 'v'
// (v6.17.5) and one is typo'd with 'v.' (v.6.15.1).
const normalizeTag = (tag: string) => tag.replace(/^v\.?/, "");

// Numeric compare so 6.18.1 sorts above 6.9.0. Build metadata (6.17.9+1) is
// ignored for ordering but kept in the string we display.
const compareVersions = (a: string, b: string) => {
  const parts = (version: string) =>
    version
      .split("+")[0]
      .split(".")
      .map((part) => parseInt(part, 10) || 0);
  const partsA = parts(a);
  const partsB = parts(b);
  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const diff = (partsA[i] ?? 0) - (partsB[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

const InstallSDKFlutter = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<IProgress[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [latestSdkVersion, setLatestSdkVersion] = useState(
    FALLBACK_SDK_VERSION
  );

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    const state: ISessionProps = location.state as ISessionProps;
    setProgress(state?.progress);
  }, [location.state]);

  useEffect(() => {
    const fetchLatestVersion = async () => {
      try {
        // Using GitHub API which has CORS support
        const versions: string[] = [];

        // Page 1 is enough while 6.x is still recent, but keep paging so the
        // snippet doesn't silently fall back once newer majors push it down.
        for (let page = 1; page <= MAX_RELEASE_PAGES; page++) {
          const response = await fetch(
            `${RELEASES_URL}?per_page=${RELEASES_PER_PAGE}&page=${page}`
          );
          if (!response.ok) {
            throw new Error("Failed to fetch releases");
          }
          const releases = await response.json();
          if (!Array.isArray(releases) || releases.length === 0) {
            break;
          }

          releases
            .filter((release) => !release?.draft && !release?.prerelease)
            .map((release) => normalizeTag(release?.tag_name ?? ""))
            .filter((version) => version.startsWith(SDK_MAJOR_PREFIX))
            .forEach((version) => versions.push(version));

          if (versions.length > 0 || releases.length < RELEASES_PER_PAGE) {
            break;
          }
        }

        if (versions.length > 0) {
          versions.sort(compareVersions);
          setLatestSdkVersion(versions[versions.length - 1]);
        }
      } catch (error) {
        console.error("Failed to fetch latest SDK version:", error);
      }
    };

    fetchLatestVersion();
  }, []);

  const handleClick = () => {
    const progressArr: IProgress[] = progress.slice();
    progressArr.push({
      id: "startsdk_startsdk",
      text: "progress_startsdk_startSDK",
      order: 3,
    });
    // Initialize progress with a copy of progressArr
    const progressState: ISessionProps = {
      ...location.state,
      progress: progressArr,
      currentPage: location.state?.currentPage + 1,
    };

    amplitudeTrack_continue(location.state)

    // Navigate directly to startsdk, skipping setpermissions, referrer, and proguard
    navigate(`/startsdk/${location.state.os}/startsdk`, {
      state: progressState,
    });
  };

  const codeStep1 = `dependencies:
  flutter:
    sdk: flutter
  appsflyer_sdk: ^${latestSdkVersion}`;

  const steps = [
    {
      label: t("StartSDK_installSDK_flutter_step1_label"),
      component: (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <Trans
                i18nKey="StartSDK_installSDK_flutter_step1_description"
                components={{ code: <code /> }}
              />
            </Typography>
            <DJSimpleCodeBlock
              codePrimary={codeStep1}
              showLineNumbers={true}
              language="yaml"
              highlight="4"
            />
          </Box>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("StartSDK_installSDK_flutter_step2_label"),
      component: (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Run:
            </Typography>
            <DJSimpleCodeBlock
              codePrimary={"flutter pub get"}
              showLineNumbers={false}
            />
          </Box>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
  ];

  return (
    <>
      <Container maxWidth={"lg"}>
        <Box padding={3} style={{ minHeight: "50vh" }}>
          <Typography variant="h1">
            {t("startSDK_InstallSDK_flutter_title")}
          </Typography>
          

          <Stepper
            activeStep={activeStep}
            style={{ border: "none" }}
            orientation="vertical"
          >
            {steps.map(
              ({ label, component, shouldDisplayNavigationButtons }) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                  <StepContent>
                    {component}
                    {shouldDisplayNavigationButtons && (
                      <Box sx={{ mt: 2 }}>
                        {activeStep !== 0 && (
                          <Button
                            variant="text"
                            color="primary"
                            onClick={handleBack}
                            // sx={{ mt: 2, mr: 1 }}
                          >
                            {t("general_Back")}
                          </Button>
                        )}
                        {activeStep !== steps.length - 1 && (
                          <Button
                            // sx={{ mt: 2 }}
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                          >
                            {activeStep === steps.length - 1
                              ? "Finish"
                              : t("general_Next")}
                          </Button>
                        )}
                      </Box>
                    )}
                  </StepContent>
                </Step>
              )
            )}
          </Stepper>
        </Box>
        <Box
          display={"flex"}
          width={"100%"}
          justifyContent="space-between"
          alignSelf="end"
          justifySelf="end"
          marginTop={10}
          textAlign="end"
        >
          {" "}
          <Button
            variant="outlined"
            size="medium"
            color="secondary"
            style={{ marginRight: "5px" }}
            onClick={() => {
              const progressState: ISessionProps = {
                ...location.state,
                currentPage: location.state?.currentPage - 1,
              };
              navigate(`/integrationPath`, {
                state: progressState,
                replace: true,
              });
            }}
          >
            {t("general_Back")}
          </Button>
          <Button
            variant="contained"
            size="medium"
            color="primary"
            onClick={() => handleClick()}
          >
            {t("general_Continue")}
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default transition(InstallSDKFlutter);
